// Realistic Apple Wallet & Google Wallet pass renderings.
// Not affiliated with Apple or Google — clearly labelled as a preview.
import { memo } from "react";
import { motion } from "framer-motion";
import { iconForBusiness } from "./BusinessMark";

function hexToRgb(h) {
  const m = h.replace("#", "");
  const v = m.length === 3 ? m.split("").map((c) => c + c).join("") : m;
  return [parseInt(v.slice(0, 2), 16), parseInt(v.slice(2, 4), 16), parseInt(v.slice(4, 6), 16)];
}
function isLight(hex) {
  try { const [r, g, b] = hexToRgb(hex); return (0.299 * r + 0.587 * g + 0.114 * b) > 150; } catch { return false; }
}
function mix(hex, other, t) {
  const a = hexToRgb(hex), b = hexToRgb(other);
  return `rgb(${a.map((v, i) => Math.round(v + (b[i] - v) * t)).join(",")})`;
}

function QrMatrix({ size = 84, seed = "BP", fg = "#111" }) {
  const n = 25;
  let s = 0; for (let i = 0; i < seed.length; i++) s += seed.charCodeAt(i) * (i + 7);
  const rand = (i) => { const x = Math.sin(s + i * 12.9898) * 43758.5453; return x - Math.floor(x); };
  const finder = (x, y) => (x < 7 && y < 7) || (x > n - 8 && y < 7) || (x < 7 && y > n - 8);
  const cells = [];
  for (let y = 0; y < n; y++) for (let x = 0; x < n; x++) {
    if (finder(x, y)) continue;
    if (rand(y * n + x) > 0.52) cells.push([x, y]);
  }
  const u = size / n;
  const Finder = ({ x, y }) => (
    <g>
      <rect x={x * u} y={y * u} width={7 * u} height={7 * u} fill={fg} />
      <rect x={(x + 1) * u} y={(y + 1) * u} width={5 * u} height={5 * u} fill="#fff" />
      <rect x={(x + 2) * u} y={(y + 2) * u} width={3 * u} height={3 * u} fill={fg} />
    </g>
  );
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} shapeRendering="crispEdges">
      {cells.map(([x, y], i) => <rect key={i} x={x * u} y={y * u} width={u} height={u} fill={fg} />)}
      <Finder x={0} y={0} /><Finder x={n - 7} y={0} /><Finder x={0} y={n - 7} />
    </svg>
  );
}

function Pdf417({ fg = "#111", seed = "BP" }) {
  let s = 3; for (let i = 0; i < seed.length; i++) s += seed.charCodeAt(i);
  const rows = 6, cols = 52, items = [];
  for (let r = 0; r < rows; r++) for (let c = 0; c < cols; c++) {
    if (Math.abs(Math.sin(s + r * 7.1 + c * 3.3)) > 0.5) items.push(<rect key={`${r}-${c}`} x={c * (100 / cols)} y={r * 16} width={100 / cols} height={16} fill={fg} />);
  }
  return <svg width="100%" height="36" viewBox={`0 0 100 ${rows * 16}`} preserveAspectRatio="none" shapeRendering="crispEdges">{items}</svg>;
}

const Check = ({ c }) => <svg width="11" height="11" viewBox="0 0 24 24" fill="none"><path d="M20 6 9 17l-5-5" stroke={c} strokeWidth="3.6" strokeLinecap="round" strokeLinejoin="round" /></svg>;

const StampGrid = memo(function StampGrid({ threshold, balance, accent, text, onAccent }) {
  return (
    <div className="grid grid-cols-5 gap-[7px]">
      {Array.from({ length: Math.min(threshold, 10) }).map((_, i) => (
        <motion.div key={i} initial={{ scale: 0.4, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ delay: i * 0.02, type: "spring", stiffness: 320, damping: 18 }}
          className="aspect-square rounded-full grid place-items-center" style={{ border: `1.5px solid ${text}2e`, background: i < balance ? accent : "transparent" }}>
          {i < balance ? <Check c={onAccent} /> : <span style={{ color: text, opacity: 0.3, fontSize: 8.5, fontWeight: 600 }}>{i + 1}</span>}
        </motion.div>
      ))}
    </div>
  );
});

const Field = ({ label, value, color, align = "left", size = 14 }) => (
  <div style={{ textAlign: align, minWidth: 0 }}>
    <div className="uppercase" style={{ color, opacity: 0.55, fontSize: 8, letterSpacing: "0.09em", fontWeight: 600 }}>{label}</div>
    <div className="truncate" style={{ color, fontSize: size, lineHeight: 1.25, fontWeight: 600, marginTop: 1 }}>{value}</div>
  </div>
);

// Back-of-pass field row — label above value, with a divider line.
// Matches the aesthetic Apple Wallet uses for its back fields.
const BackRow = memo(function BackRow({ label, value, color, accent, isFirst = false }) {
  if (!value) return null;
  return (
    <div style={{ borderTop: isFirst ? "none" : `1px solid ${color}14`, paddingTop: isFirst ? 0 : 10, paddingBottom: 10 }}>
      <div className="uppercase" style={{ color: accent, opacity: 0.85, fontSize: 8.5, letterSpacing: "0.14em", fontWeight: 700 }}>{label}</div>
      <div style={{ color, fontSize: 12, lineHeight: 1.45, marginTop: 3, wordBreak: "break-word", whiteSpace: "pre-wrap" }}>{value}</div>
    </div>
  );
});

export const WalletPass = memo(function WalletPass({ program = {}, wallet = "apple", state = {}, face = "front", onFlip, className = "" }) {
  const bg = program.brand_color || "#0B0D10";
  const accent = program.secondary_color || "#C8FF4D";
  // Honor the merchant's explicit Text color from the designer; fall back to
  // luminance-derived. The .pkpass builder applies the exact same rule.
  const text = program.text_color || (isLight(bg) ? "#0B0D10" : "#FFFFFF");
  const type = program.type || "stamp";
  const threshold = program.reward?.threshold || 10;
  const balance = state.balance ?? 4;
  const visits = state.visits ?? balance;
  const points = state.points ?? balance * 10;
  const rewardName = program.reward?.name || "Free reward";
  const passNo = state.pass_number || "BP-00042";
  const org = program.org_name || program.pass_title || "Your Business";
  const title = program.pass_title || program.name || "Loyalty";
  const rewardAvailable = state.reward_status === "available" || balance >= threshold;
  const onAccent = isLight(accent) ? "#0B0D10" : "#FFFFFF";
  // Fallback tile shows the business initials — identical to the monogram
  // tile the .pkpass generator draws, so designer === wallet.
  const initials = (org || "B").split(/\s+/).map((w) => w[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();
  const hasLogo = !!program.logo_url;
  const hasStrip = !!program.hero_url;

  // Compose back-of-pass content on the fly. This matches the fields the real
  // .pkpass emits so previews are true-to-life. Skips empty rows.
  const howItWorks = `Show this pass every visit and we'll add a ${type === "stamp" ? "stamp" : type === "points" ? "point" : "visit"}. At ${threshold} ${type === "stamp" ? "stamps" : type === "points" ? "points" : "visits"} you unlock ${rewardName}. Your pass updates instantly on your phone.`;
  const backRows = [
    { label: "How it works", value: howItWorks },
    { label: "About", value: program.about_note || "" },
    { label: "Terms", value: program.terms || "" },
    { label: "Website", value: program.website_url || "" },
    { label: "Email", value: program.support_email || "" },
    { label: "Phone", value: program.support_phone || "" },
    { label: "Member since", value: (state.issued_at ? new Date(state.issued_at) : new Date()).toLocaleDateString("en-US", { month: "short", year: "numeric" }).toUpperCase() },
  ].filter((r) => r.value);

  // ---------------- APPLE ----------------
  if (wallet === "apple") {
    const tier = program.membership_tier || "Gold";
    const headerField = type === "points" ? ["Balance", `${points}`] : type === "membership" ? ["Member", tier] : ["Reward", rewardAvailable ? "Ready" : `${balance}/${threshold}`];
    const showBack = face === "back";
    const flip = () => { if (onFlip) onFlip(showBack ? "front" : "back"); };
    return (
      <div className={`w-[300px] select-none ${className}`} style={{ fontFamily: "'SF Pro Text',-apple-system,'Inter',sans-serif", perspective: 1400 }}>
        <motion.div
          animate={{ rotateY: showBack ? 180 : 0 }}
          transition={{ type: "spring", stiffness: 160, damping: 22 }}
          style={{ transformStyle: "preserve-3d", position: "relative" }}
          onClick={onFlip ? flip : undefined}
          role={onFlip ? "button" : undefined}
          tabIndex={onFlip ? 0 : undefined}
        >
          {/* FRONT */}
          <div className="rounded-[19px] overflow-hidden shadow-[0_20px_50px_-16px_rgba(0,0,0,0.45)]" style={{ background: bg, backfaceVisibility: "hidden", cursor: onFlip ? "pointer" : "default" }}>
            <div className="px-[15px] pt-[13px] pb-[11px]">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 min-w-0">
                  {hasLogo ? (
                    <img src={program.logo_url} alt="" className="shrink-0 object-contain" style={{ height: 24, maxWidth: 60 }} />
                  ) : (
                    <div className="rounded-[7px] grid place-items-center shrink-0" style={{ width: 24, height: 24, background: accent, color: onAccent }}>
                      <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: "-0.02em" }}>{initials}</span>
                    </div>
                  )}
                  <span className="truncate" style={{ color: text, fontSize: 12.5, fontWeight: 600 }}>{org}</span>
                </div>
                {type !== "membership" && (
                  <Field label={headerField[0]} value={headerField[1]} color={text} align="right" size={13} />
                )}
              </div>

              {hasStrip && (
                <div className="mt-3 -mx-[15px] relative overflow-hidden" style={{ height: 76 }}>
                  <img src={program.hero_url} alt="" className="w-full h-full object-cover" />
                  <div className="absolute inset-x-0 bottom-0 h-1/2" style={{ background: `linear-gradient(to bottom, transparent, ${bg}55, ${bg})` }} />
                </div>
              )}

              <div className="mt-3.5">
                {type === "stamp" ? (
                  <>
                    <div className="uppercase" style={{ color: text, opacity: 0.5, fontSize: 8, letterSpacing: "0.11em", fontWeight: 600, marginBottom: 8 }}>{title}</div>
                    <StampGrid threshold={threshold} balance={balance} accent={accent} text={text} onAccent={onAccent} />
                  </>
                ) : type === "points" ? (
                  <>
                    <div className="uppercase" style={{ color: text, opacity: 0.5, fontSize: 8, letterSpacing: "0.11em", fontWeight: 600, marginBottom: 8 }}>{title}</div>
                    <div className="flex items-end gap-2"><span className="font-display" style={{ color: text, fontSize: 42, fontWeight: 800, lineHeight: 0.9, letterSpacing: "-0.02em" }}>{points}</span><span style={{ color: text, opacity: 0.5, fontSize: 12, marginBottom: 4 }}>points</span></div>
                  </>
                ) : type === "visit" ? (
                  <>
                    <div className="uppercase" style={{ color: text, opacity: 0.5, fontSize: 8, letterSpacing: "0.11em", fontWeight: 600, marginBottom: 8 }}>{title}</div>
                    <div className="flex items-end gap-2"><span className="font-display" style={{ color: text, fontSize: 42, fontWeight: 800, lineHeight: 0.9 }}>{visits}</span><span style={{ color: text, opacity: 0.5, fontSize: 13, marginBottom: 5 }}>/ {threshold} visits</span></div>
                  </>
                ) : (
                  // Membership: matches Apple Wallet's real primaryField layout.
                  // Tiny "MEMBER" label above the big tier value, no separate
                  // right-side header field (dropped in header row above).
                  <div>
                    <div className="uppercase" style={{ color: text, opacity: 0.5, fontSize: 8.5, letterSpacing: "0.11em", fontWeight: 600, marginBottom: 4 }}>Member</div>
                    <span className="font-display" style={{ color: text, fontSize: 34, fontWeight: 800, lineHeight: 0.95, letterSpacing: "-0.02em" }}>{tier}</span>
                  </div>
                )}
              </div>

              <div className="mt-3.5 pt-3 grid grid-cols-2 gap-2" style={{ borderTop: `1px solid ${text}18` }}>
                <Field label={type === "membership" ? "Perks" : rewardAvailable ? "Available now" : "Next reward"} value={rewardName} color={text} size={12.5} />
                <Field label="Pass" value={passNo} color={text} align="right" size={12.5} />
              </div>
            </div>

            <div className="bg-white mx-[15px] mb-[15px] rounded-[10px] px-3.5 py-3 flex flex-col items-center">
              <QrMatrix size={100} seed={passNo} fg="#1a1a1a" />
              <span className="mt-2 tracking-[0.3em]" style={{ color: "#222", fontSize: 8.5 }}>{passNo}</span>
            </div>
          </div>

          {/* BACK — absolutely positioned, rotated 180° so it faces us when flipped */}
          <div className="absolute inset-0 rounded-[19px] overflow-hidden shadow-[0_20px_50px_-16px_rgba(0,0,0,0.45)]" style={{ background: bg, backfaceVisibility: "hidden", transform: "rotateY(180deg)", cursor: onFlip ? "pointer" : "default" }}>
            <div className="px-[16px] pt-[13px] pb-[16px] h-full flex flex-col">
              <div className="flex items-center justify-between pb-2.5" style={{ borderBottom: `1px solid ${text}14` }}>
                <div className="flex items-center gap-2 min-w-0">
                  {hasLogo ? (
                    <img src={program.logo_url} alt="" className="shrink-0 object-contain" style={{ height: 22, maxWidth: 54 }} />
                  ) : (
                    <div className="rounded-[7px] grid place-items-center shrink-0" style={{ width: 22, height: 22, background: accent, color: onAccent }}>
                      <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: "-0.02em" }}>{initials}</span>
                    </div>
                  )}
                  <span className="truncate" style={{ color: text, fontSize: 12, fontWeight: 600 }}>{org}</span>
                </div>
                <span className="uppercase" style={{ color: text, opacity: 0.5, fontSize: 9, letterSpacing: "0.12em", fontWeight: 600 }}>Info</span>
              </div>
              <div className="mt-2 overflow-y-auto flex-1" style={{ maxHeight: 420 }}>
                {backRows.map((r, i) => <BackRow key={r.label} label={r.label} value={r.value} color={text} accent={accent} isFirst={i === 0} />)}
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  // ---------------- GOOGLE ----------------
  const gTier = program.membership_tier || "Gold";
  const gTop = mix(bg, "#000000", 0.06);
  const gShowBack = face === "back";
  const gFlip = () => { if (onFlip) onFlip(gShowBack ? "front" : "back"); };
  return (
    <div className={`w-[300px] select-none ${className}`} style={{ fontFamily: "'Roboto','Google Sans','Inter',sans-serif", perspective: 1400 }}>
      <motion.div
        animate={{ rotateY: gShowBack ? 180 : 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        style={{ transformStyle: "preserve-3d", position: "relative" }}
      >
        <div className="rounded-[18px] overflow-hidden shadow-[0_20px_50px_-16px_rgba(0,0,0,0.4)]" style={{ background: gTop, backfaceVisibility: "hidden", cursor: onFlip ? "pointer" : "default" }}>
          <div className="px-4 pt-4 pb-4" onClick={gFlip}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5 min-w-0">
                {hasLogo ? (
                  <img src={program.logo_url} alt="" className="shrink-0 object-contain" style={{ height: 30, maxWidth: 72 }} />
                ) : (
                  <div className="rounded-full grid place-items-center shrink-0" style={{ width: 30, height: 30, background: accent, color: onAccent }}>
                    <span style={{ fontSize: 12, fontWeight: 800, letterSpacing: "-0.02em" }}>{initials}</span>
                  </div>
                )}
                <div className="min-w-0">
                  <div className="truncate" style={{ color: text, fontSize: 13.5, fontWeight: 500, lineHeight: 1.1 }}>{org}</div>
                  <div className="truncate" style={{ color: text, opacity: 0.6, fontSize: 10.5 }}>{title}</div>
                </div>
              </div>
              <div className="flex flex-col gap-[3px] pt-1">{[0, 1, 2].map((i) => <span key={i} className="rounded-full" style={{ width: 3.5, height: 3.5, background: text, opacity: 0.55 }} />)}</div>
            </div>

            {/* Rows mirror the real Loyalty Object: loyaltyPoints | secondaryLoyaltyPoints, then accountName | accountId */}
            <div className="mt-4 grid grid-cols-2 gap-y-3">
              {type === "membership" ? (
                <>
                  <Field label="Member" value={gTier} color={text} size={13} />
                  <Field label="Perks" value={rewardName} color={text} align="right" size={13} />
                </>
              ) : (
                <>
                  <Field label={type === "points" ? "Points" : type === "visit" ? "Visits" : "Stamps"} value={type === "points" ? `${points}` : `${balance} / ${threshold}`} color={text} size={13} />
                  <Field label={rewardAvailable ? "Available now" : "Next reward"} value={rewardName} color={text} align="right" size={13} />
                </>
              )}
              <Field label="Pass" value={title} color={text} size={13} />
              <Field label="Number" value={passNo} color={text} align="right" size={13} />
            </div>
          </div>

          <div className="bg-white mx-3 mb-3 rounded-[14px] py-3 flex flex-col items-center">
            <QrMatrix size={96} seed={passNo} fg="#1a1a1a" />
            <span className="mt-1.5 tracking-[0.25em]" style={{ color: "#444", fontSize: 8.5 }}>{passNo}</span>
          </div>

          {/* Hero banner (Google draws it under the barcode) = the same strip the Apple pass carries */}
          {type !== "membership" && (
            <div className="relative px-5 pt-3 pb-4 overflow-hidden" style={{ background: bg }} data-testid="google-preview-hero">
              {hasStrip && <img src={program.hero_url} alt="" className="absolute inset-0 w-full h-full object-cover" style={{ opacity: 0.2 }} />}
              <div className="relative">
                <div className="uppercase" style={{ color: text, opacity: 0.5, fontSize: 8.5, letterSpacing: "0.22em", fontWeight: 700 }}>{title}</div>
                {type === "stamp" ? (
                  <div className="mt-2"><StampGrid threshold={threshold} balance={balance} accent={accent} text={text} onAccent={onAccent} /></div>
                ) : (
                  <div className="mt-1 flex items-baseline gap-1.5"><span className="font-display" style={{ color: text, fontSize: 30, fontWeight: 800, lineHeight: 1 }}>{type === "points" ? points : visits}</span><span style={{ color: text, opacity: 0.5, fontSize: 12 }}>{type === "points" ? "points" : `/ ${threshold} visits`}</span></div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Google back-of-pass: appears when tapped. Google Wallet expands the
            card into a details sheet — we render the equivalent info cards
            merchants configured on the "back" tab. */}
        <div className="absolute inset-0 rounded-[18px] overflow-hidden shadow-[0_20px_50px_-16px_rgba(0,0,0,0.4)]" style={{ background: gTop, backfaceVisibility: "hidden", transform: "rotateY(180deg)", cursor: onFlip ? "pointer" : "default" }} onClick={gFlip}>
          <div className="px-4 pt-4 pb-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2.5 min-w-0">
                {hasLogo ? (
                  <img src={program.logo_url} alt="" className="shrink-0 object-contain" style={{ height: 28, maxWidth: 68 }} />
                ) : (
                  <div className="rounded-full grid place-items-center shrink-0" style={{ width: 28, height: 28, background: accent, color: onAccent }}>
                    <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: "-0.02em" }}>{initials}</span>
                  </div>
                )}
                <div className="min-w-0"><div className="truncate" style={{ color: text, fontSize: 13, fontWeight: 500, lineHeight: 1.1 }}>{org}</div></div>
              </div>
              <span style={{ color: text, opacity: 0.5, fontSize: 10.5, textTransform: "uppercase", letterSpacing: 1 }}>Details</span>
            </div>
            <div style={{ maxHeight: 320, overflowY: "auto" }}>
              {backRows.map((r, i) => <BackRow key={r.label} label={r.label} value={r.value} color={text} accent={accent} isFirst={i === 0} />)}
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
});
